import React, { forwardRef } from "react"
import { twMerge } from "tailwind-merge"
import { motion } from "framer-motion"

import { Text } from "@components/ui"
import Required from "./Required"

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  icon?: React.ReactNode
  rightIcon?: React.ReactNode
  onRightIconClick?: () => void
  classNameWrapper?: string
  errorStyled?: boolean
  leftIcon?: React.ReactNode
  onLeftIconClick?: () => void
  required?: boolean
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      icon,
      rightIcon,
      onRightIconClick,
      className = "",
      classNameWrapper = "",
      errorStyled = false,
      leftIcon,
      onLeftIconClick,
      required = false,
      ...props
    },
    ref,
  ) => {
    const baseClasses =
      "w-full px-3 py-2 border rounded-lg transition-all duration-200 focus:outline-none focus:ring-legacy-blue focus:border-transparent bg-white dark:bg-legacy-navy text-legacy-dark dark:text-legacy-cream"
    const errorClasses =
      error || errorStyled
        ? "border-red-500 focus:ring-red-500"
        : "border-legacy-light dark:border-legacy-light/30"
    const iconClasses = icon || leftIcon ? "pl-10" : ""
    const rightIconClasses = rightIcon ? "pr-10" : ""
    const leftIconClasses = leftIcon ? "pl-10" : ""

    const classes = twMerge(
      "disabled:opacity-50 disabled:cursor-not-allowed",
      "enabled:focus:placeholder:translate-x-1 placeholder:transition-all placeholder:duration-200 placeholder:text-slate-300",
      baseClasses,
      errorClasses,
      iconClasses,
      rightIconClasses,
      leftIconClasses,
      className,
    )

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className={twMerge(classNameWrapper)}
      >
        {label && (
          <Text as="label" htmlFor={props.id} color="legacy-dark" className="mb-0">
            {label} {required && <Required />}
          </Text>
        )}
        <div className="relative">
          {icon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">{icon}</div>
          )}
          {leftIcon && (
            <button
              type="button"
              className="absolute inset-y-0 left-0 pl-3 flex items-center"
              onClick={onLeftIconClick}
            >
              {leftIcon}
            </button>
          )}
          <input ref={ref} className={classes} {...props} />
          {rightIcon && !!onRightIconClick && (
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={onRightIconClick}
            >
              {rightIcon}
            </button>
          )}
          {rightIcon && !onRightIconClick && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              {rightIcon}
            </div>
          )}
        </div>
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm text-red-600 dark:text-red-400"
          >
            {error}
          </motion.p>
        )}
      </motion.div>
    )
  },
)

export default Input
